// client/src/components/WatermarkPosition.jsx
import { useState, useEffect } from 'react';

const POSITIONS = [
  { key: 'top-left',    label: '↖', x: 3,   y: 3 },
  { key: 'top-center',  label: '↑',  x: 50, y: 3 },
  { key: 'top-right',   label: '↗', x: 97,  y: 3 },
  { key: 'middle-left', label: '←',  x: 3,   y: 50 },
  { key: 'center',      label: '●',  x: 50, y: 50 },
  { key: 'middle-right',label: '→',  x: 97,  y: 50 },
  { key: 'bottom-left', label: '↙', x: 3,   y: 97 },
  { key: 'bottom-center',label: '↓', x: 50, y: 97 },
  { key: 'bottom-right',label: '↘', x: 97,  y: 97 },
];

export default function WatermarkPosition({ type, position, rotation, size = 50, text: externalText, onChange }) {
  const [text, setText] = useState(externalText || '');

  // Aggiorna lo stato interno quando il prop cambia
  useEffect(() => {
    if (externalText !== undefined) {
      setText(externalText);
    }
  }, [externalText]);

  function handlePosition(key) {
    const pos = POSITIONS.find(p => p.key === key);
    if (pos) {
      onChange({ position: key, x: pos.x, y: pos.y });
    }
  }

  function handleRotation(deg) {
    onChange({ rotation: deg });
  }

  function handleSize(val) {
    onChange({ size: val });
  }

  function handleTextChange(value) {
    setText(value);
    onChange({ _text: value });
  }

  // Anteprima: la dimensione (10-100%) viene scalata sull'altezza del riquadro
  const previewFontSize = Math.max(6, Math.round((size / 100) * 22));
  const previewLabel = type === 'text' ? (text || 'WATERMARK') : 'LOGO';

  return (
    <div className="space-y-3">
      {/* Toggle tipo + testo in una riga */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-surface-4">Tipo:</span>
        <div className="flex rounded-lg overflow-hidden border border-line">
          <button
            className={`px-3 py-1.5 text-sm ${type === 'logo' ? 'bg-brand-600 text-white' : 'bg-panel text-surface-3'}`}
            onClick={() => onChange({ type: 'logo' })}
          >
            🖼 Logo
          </button>
          <button
            className={`px-3 py-1.5 text-sm ${type === 'text' ? 'bg-brand-600 text-white' : 'bg-panel text-surface-3'}`}
            onClick={() => onChange({ type: 'text', position: 'center', rotation: -45 })}
          >
            📝 Testo
          </button>
        </div>
      </div>

      {/* Rettangolo posizione compatto */}
      <div className="relative w-56 h-44 bg-black/20 rounded-lg border border-line overflow-hidden">
        {/* Griglia di sfondo */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(var(--color-line) 1px, transparent 1px), linear-gradient(90deg, var(--color-line) 1px, transparent 1px)',
          backgroundSize: '25% 25%',
        }} />

        {/* Pallini posizione */}
        {POSITIONS.map(p => {
          const isActive = position === p.key;
          return (
            <button
              key={p.key}
              className={`absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 transition-all flex items-center justify-center text-[10px] ${
                isActive
                  ? 'bg-brand-500 border-white text-white scale-125 shadow-lg'
                  : 'bg-panel/80 border-line text-surface-4 hover:border-brand-400 hover:text-brand-300'
              }`}
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
              onClick={() => handlePosition(p.key)}
              title={p.key}
            >
              {p.label}
            </button>
          );
        })}

        {/* Anteprima watermark */}
        <div
          className="absolute pointer-events-none transition-all duration-200"
          style={{
            left: `${POSITIONS.find(p => p.key === position)?.x || 50}%`,
            top: `${POSITIONS.find(p => p.key === position)?.y || 50}%`,
            transform: `translate(-50%, -50%) rotate(${rotation || 0}deg)`,
            opacity: 0.15,
            fontSize: `${previewFontSize}px`,
            fontWeight: 'bold',
            color: '#fff',
            whiteSpace: 'nowrap',
          }}
        >
          {previewLabel}
        </div>
      </div>

      {/* Slider dimensione e rotazione — valide sia per logo che per testo */}
      <div className="space-y-3 rounded-lg border border-line bg-panel/40 p-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-surface-4">Dimensione</label>
            <span className="text-xs font-medium text-brand-300 tabular-nums">{size}%</span>
          </div>
          <input
            type="range"
            min="10"
            max="100"
            step="5"
            value={size}
            onChange={e => handleSize(Number(e.target.value))}
            className="w-full accent-brand-500"
          />
          <div className="flex justify-between text-[10px] text-surface-4 mt-0.5">
            <span>10%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-surface-4">Rotazione</label>
            <span className="text-xs font-medium text-brand-300 tabular-nums">{rotation ?? -45}°</span>
          </div>
          <input
            type="range"
            min="-90"
            max="90"
            step="1"
            value={rotation ?? -45}
            onChange={e => handleRotation(Number(e.target.value))}
            className="w-full accent-brand-500"
          />
          <div className="mt-1.5 flex gap-1.5">
            <button className="btn btn-ghost text-xs px-2 py-1" onClick={() => handleRotation(-45)}>-45°</button>
            <button className="btn btn-ghost text-xs px-2 py-1" onClick={() => handleRotation(0)}>0°</button>
            <button className="btn btn-ghost text-xs px-2 py-1" onClick={() => handleRotation(-90)}>-90°</button>
          </div>
        </div>
      </div>

      {/* Campo testo — solo per watermark a testo */}
      {type === 'text' && (
        <div>
          <label className="block text-xs text-surface-4 mb-1">Testo watermark</label>
          <input
            className="input"
            value={text}
            onChange={e => handleTextChange(e.target.value)}
            placeholder="es. BOZZA - Non valido"
          />
        </div>
      )}

      {/* Stato */}
      <div className="text-xs text-surface-4">
        Posizione: <span className="text-brand-300 capitalize">{position || 'center'}</span>
        {' | '} Rotazione: <span className="text-brand-300">{rotation ?? -45}°</span>
        {' | '} Dimensione: <span className="text-brand-300">{size}%</span>
        {' | '} Tipo: <span className="text-brand-300">{type || 'logo'}</span>
      </div>
    </div>
  );
}
